// Manual "Kits" (2026-11-02). Se muestra en Ayuda y en /ayuda/imprimir?manual=kits (versión sin
// menú de donde sale el PDF public/ayuda/Manual_Kits.pdf). Imágenes en public/ayuda/kits/.
const Paso = ({ n, children }: { n: number; children: React.ReactNode }) => (
  <li className="flex gap-3 items-start">
    <span className="shrink-0 w-7 h-7 rounded-full bg-blue-600 text-white text-sm font-bold flex items-center justify-center">{n}</span>
    <div className="pt-0.5">{children}</div>
  </li>
);
const Imagen = ({ src, alt, ancho = 'max-w-2xl' }: { src: string; alt: string; ancho?: string }) => (
  <img src={`/ayuda/kits/${src}`} alt={alt} className={`${ancho} w-full border rounded-lg shadow-sm my-3 manual-imagen`} />
);
const Seccion = ({ titulo, children }: { titulo: string; children: React.ReactNode }) => (
  <section className="mb-8 manual-seccion">
    <h2 className="text-xl font-bold text-blue-800 border-b-2 border-blue-200 pb-1 mb-3">{titulo}</h2>
    <div className="space-y-3 text-gray-800 leading-relaxed">{children}</div>
  </section>
);
const Aviso = ({ children, color = 'amber' }: { children: React.ReactNode; color?: 'amber' | 'red' | 'green' | 'blue' }) => {
  const c = { amber: 'bg-amber-50 border-amber-400', red: 'bg-red-50 border-red-400', green: 'bg-green-50 border-green-500', blue: 'bg-blue-50 border-blue-400' }[color];
  return <div className={`border-l-4 ${c} px-4 py-2 rounded`}>{children}</div>;
};

const ManualKits = () => (
  <div className="manual">
    <div className="mb-8">
      <h1 className="text-3xl font-bold text-gray-900">Kits</h1>
      <p className="text-gray-600">Sistema Rápido · Manual de uso · Actualizado el 02/11/2026</p>
    </div>

    <Seccion titulo="1. ¿Qué es un kit?">
      <p>Un <b>kit</b> es un producto formado por varias piezas que ya están en el almacén. Por ejemplo, un kit de reparo
        puede llevar 2 pistones, 4 jebes y 1 juego de pernos.</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><b>El kit no tiene stock propio.</b> El sistema calcula cuántos kits se pueden armar con las piezas que hay.</li>
        <li><b>Vender un kit</b> descuenta todas sus piezas a la vez.</li>
        <li><b>Vender una pieza sola</b> es una venta normal: baja solo esa pieza, y los kits que se pueden armar bajan solos.</li>
        <li>El kit tiene <b>su propio precio de venta</b>, que puede ser distinto a la suma de sus piezas.</li>
      </ul>
      <Aviso color="blue">Ningún producto se volvió kit por sí solo. Los productos que se llaman "KIT" o "JUEGO" se revisan
        uno por uno y se marcan como kit solo si de verdad están formados por piezas del almacén.</Aviso>
    </Seccion>

    <Seccion titulo="2. Convertir un producto en kit">
      <ol className="space-y-3">
        <Paso n={1}>En <b>Stock</b>, busca el producto y toca <b>Editar</b>.</Paso>
        <Paso n={2}>Al final de la edición marca la casilla <b>"Es un KIT (formado por piezas del almacén)"</b>.</Paso>
        <Paso n={3}>En <b>"Buscar pieza…"</b> escribe el nombre o código de cada pieza y tócala para agregarla.</Paso>
        <Paso n={4}>A la izquierda de cada pieza pon <b>cuántas lleva cada kit</b>. Con el tacho rojo se quita una pieza.</Paso>
        <Paso n={5}>Revisa los <b>kits armables</b> y la <b>suma de las piezas</b> comparada con el precio del kit.</Paso>
        <Paso n={6}>Toca <b>Guardar Cambios</b>.</Paso>
      </ol>
      <Imagen src="02_editor_kit.png" alt="Edición de un producto marcado como kit" />
      <Aviso>
        <p><b>Si el producto ya tenía unidades</b> (por ejemplo, 8 kits contados en el almacén), aparece un aviso para
          <b> pasarlas a sus piezas</b>: a cada pieza se le suman 8 × su cantidad. Déjalo marcado si esos kits existen de verdad.
          Si no existen, desmárcalo y esas unidades se dejan de contar.</p>
      </Aviso>
      <ul className="list-disc pl-6 space-y-1">
        <li>Un kit no puede llevar otro kit adentro, ni ser pieza de sí mismo.</li>
        <li>Una pieza que ya forma parte de un kit no puede volverse kit.</li>
        <li>En la edición de un kit, el stock no se escribe a mano: dice <i>"Es un kit: el stock se calcula con sus piezas"</i>.</li>
      </ul>
    </Seccion>

    <Seccion titulo="3. Ver los kits en Stock">
      <p>En la lista, el kit lleva la etiqueta morada <b>KIT</b>, la lista de sus piezas con lo que hay de cada una y, en vez de
        "Stock Actual", dice <b>Kits armables</b>.</p>
      <Imagen src="01_lista_kit.png" alt="Kit en la lista de Stock" />
      <p>La alerta de <b>Stock bajo</b> no muestra los kits: avisa por las <b>piezas</b>, que es lo que hay que reponer.</p>
    </Seccion>

    <Seccion titulo="4. Ingresar kits que llegaron">
      <p>Cuando llegan kits armados del proveedor:</p>
      <ol className="space-y-3">
        <Paso n={1}>En <b>Stock</b>, toca <b>Editar</b> en el kit.</Paso>
        <Paso n={2}>En el recuadro <b>"Ingresar kits que llegaron armados"</b> escribe cuántos llegaron (y una observación si quieres).</Paso>
        <Paso n={3}>Toca <b>Ingresar</b>. A cada pieza se le suma su cantidad × los kits que ingresaron.</Paso>
      </ol>
      <Imagen src="04_ingresar_kits.png" alt="Ingresar kits que llegaron" />
      <Aviso color="green">Si la compra se registra en <b>Caja Repuestos</b> eligiendo el kit y marcando "sumar al stock",
        también entran sus piezas. Si esa compra se anula, las piezas se restan.</Aviso>
    </Seccion>

    <Seccion titulo="5. Vender un kit">
      <p>Se vende como cualquier producto, en <b>Ventas</b>, <b>Venta Rápida</b>, <b>Mantenimiento</b>, <b>Libro de caja</b>
        o <b>Venta pasada</b>. El kit aparece con la etiqueta <b>KIT</b> y sus <b>kits armables</b>.</p>
      <Imagen src="05_venta_kit.png" alt="Kit en la pantalla de Ventas" ancho="max-w-md" />
      <ul className="list-disc pl-6 space-y-1">
        <li>Al confirmar la venta se descuentan <b>todas sus piezas</b>.</li>
        <li>En el mismo carrito se puede vender un kit y, además, piezas sueltas que también son parte del kit.</li>
        <li>El dinero entra a caja por el <b>precio del kit</b>.</li>
      </ul>
    </Seccion>

    <Seccion titulo="6. Kit incompleto: falta una pieza">
      <p>Si al vender un kit no alcanza alguna pieza, aparece la ventana <b>"Kit incompleto"</b> con lo que falta.</p>
      <ol className="space-y-3">
        <Paso n={1}>Escribe la <b>observación</b> (por ejemplo "el cliente lleva el kit sin la faja").</Paso>
        <Paso n={2}>Escribe la <b>clave de administrador</b> y toca <b>Vender incompleto</b>.</Paso>
        <Paso n={3}>Si no se debe vender, toca <b>Cancelar</b>: no se guarda nada.</Paso>
      </ol>
      <Imagen src="06_kit_incompleto.png" alt="Ventana Kit incompleto" ancho="max-w-xs" />
      <ul className="list-disc pl-6 space-y-1">
        <li>Se descuenta <b>solo lo que hay</b> de cada pieza; nunca queda stock negativo.</li>
        <li>Si la clave está mal, la ventana vuelve a pedirla.</li>
        <li>Queda en <b>Auditoría</b> como "Kit vendido incompleto (con clave)", con la observación y las piezas que faltaron.</li>
      </ul>
    </Seccion>

    <Seccion titulo="7. Correcciones">
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Anular la venta de un kit</b> (Ventas → Historial → Anular) devuelve a cada pieza <b>exactamente</b> lo que se
          le descontó, aunque después se haya cambiado la receta del kit.</li>
        <li><b>Cambiar las piezas de un kit:</b> Editar, cambiar las piezas o cantidades y Guardar Cambios. Las ventas anteriores no cambian.</li>
        <li><b>Si no era kit:</b> Editar, desmarcar "Es un KIT" y Guardar Cambios. Vuelve a ser un producto normal (con stock 0:
          se ingresa su stock como cualquier producto).</li>
      </ul>
    </Seccion>

    <Seccion titulo="8. Auditoría">
      <p>Todo lo de kits queda registrado con el nombre de quien lo hizo:</p>
      <ul className="list-disc pl-6 space-y-1">
        <li><b>Kit creado</b> y <b>Kit modificado (piezas)</b>: qué piezas tenía antes y cuáles tiene ahora.</li>
        <li><b>Ya no es kit</b>: cuando se desmarca la casilla.</li>
        <li><b>Kits ingresados (suma piezas)</b>: cuántos kits entraron y cuánto se sumó a cada pieza.</li>
        <li><b>Kit vendido incompleto (con clave)</b>: la venta, las piezas que faltaron y la observación.</li>
      </ul>
    </Seccion>
  </div>
);

export default ManualKits;
